<script lang="ts" setup>
import Arrow from "@/assets/icons/arrow.svg?component";
import type { ComponentBlockAccordionFragment } from "~~/types/graphql";

import {
  Enum_Componentblockaccordion_Layout,
  Enum_Componentblockaccordion_Variation,
  Enum_Componentblockaccordion_Flipped,
} from "~~/types/graphql";

const { t } = useI18n();

const props = defineProps<{
  AccordionHeadline: ComponentBlockAccordionFragment["AccordionHeadline"];
  AccordionText?: ComponentBlockAccordionFragment["AccordionText"];
  AccordionVariation: Enum_Componentblockaccordion_Variation;
  AccordionLayout: Enum_Componentblockaccordion_Layout;
  AccordionItems: ComponentBlockAccordionFragment["AccordionItems"];
  AccordionFlipped: ComponentBlockAccordionFragment["AccordionFlipped"];
}>();
</script>
<i18n lang="json">
{
  "de": {
    "close": "Schließen",
    "open": "Öffnen"
  },
  "en": {
    "close": "Close",
    "open": "Open"
  }
}
</i18n>
<template>
  <layout-block
    :title="props.AccordionHeadline ?? ''"
    :class="{
      'bg-integer-black text-integer-white':
        props.AccordionVariation ===
        Enum_Componentblockaccordion_Variation.Dark,
      'bg-integer-white text-integer-black':
        props.AccordionVariation ===
        Enum_Componentblockaccordion_Variation.Light,
    }"
    class="accordion"
  >
    <div
      v-if="props.AccordionLayout === Enum_Componentblockaccordion_Layout.Half"
      class="grid grid-cols-12"
    >
      <div
        class="col-start-1 col-end-13 mb-6 mt-4 grid content-end md:row-start-1 md:m-0"
        :class="{
          'md:col-start-8':
            !(
              props.AccordionFlipped ===
              Enum_Componentblockaccordion_Flipped.Left
            ) || !props.AccordionFlipped,
          'md:col-start-1 md:col-end-6':
            props.AccordionFlipped ===
            Enum_Componentblockaccordion_Flipped.Left,
        }"
      >
        <base-text :Text="props.AccordionText ?? ''" />
      </div>
      <div
        class="col-start-1 md:row-start-1"
        :class="{
          'col-end-13 md:col-start-1 md:col-end-7':
            !(
              props.AccordionFlipped ===
              Enum_Componentblockaccordion_Flipped.Left
            ) || !props.AccordionFlipped,
          'col-end-13 md:col-start-7':
            props.AccordionFlipped ===
            Enum_Componentblockaccordion_Flipped.Left,
        }"
      >
        <module-accordion-item
          v-for="(item, index) in props.AccordionItems"
          :key="`${item?.__typename}-${index}`"
          class="group border-b"
          :class="{
            'border-b-light-gray':
              props.AccordionVariation ===
              Enum_Componentblockaccordion_Variation.Light,
            'border-b-gray':
              props.AccordionVariation ===
              Enum_Componentblockaccordion_Variation.Dark,
          }"
        >
          <template #header>
            <summary class="w-full list-none hyphens-auto py-5.5 text-h3">
              {{ item?.summary }}
            </summary>
          </template>
          <template #default>
            <base-text
              v-if="item?.details"
              :Text="item?.details"
              class="pb-5.5 pr-20"
            />
          </template>
          <template #icon="{ expand }">
            <base-button
              transparent
              only-icon
              :type="
                props.AccordionVariation ===
                Enum_Componentblockaccordion_Variation.Dark
                  ? 'primary'
                  : 'secondary'
              "
              :class="{
                'rotate-90': !expand,
                '-rotate-90': expand,
              }"
              :aria-label="expand ? t('close') : t('open')"
              ><Arrow
            /></base-button>
          </template>
        </module-accordion-item>
      </div>
    </div>
    <div
      v-if="props.AccordionLayout === Enum_Componentblockaccordion_Layout.Full"
      class="mt-6 md:mt-0"
    >
      <module-accordion-item
        v-for="(item, index) in props.AccordionItems"
        :key="`${item?.__typename}-${index}`"
        class="group border-b"
        :class="{
          'border-b-light-gray':
            props.AccordionVariation ===
            Enum_Componentblockaccordion_Variation.Light,
          'border-b-gray':
            props.AccordionVariation ===
            Enum_Componentblockaccordion_Variation.Dark,
        }"
      >
        <template #header>
          <summary class="w-full list-none py-5.5 text-h3">
            {{ item?.summary }}
          </summary>
        </template>
        <template #default>
          <base-text
            v-if="item?.details"
            :Text="item?.details"
            class="pb-5.5 pr-20"
          />
        </template>
        <template #icon="{ expand }">
          <base-button
            transparent
            only-icon
            :type="
              props.AccordionVariation ===
              Enum_Componentblockaccordion_Variation.Dark
                ? 'primary'
                : 'secondary'
            "
            :class="{
              'rotate-90': !expand,
              '-rotate-90': expand,
            }"
          >
            <Arrow
          /></base-button>
        </template>
      </module-accordion-item>
    </div>
  </layout-block>
</template>
